import React, { useState, useEffect } from 'react';
import api from '../api';

const GestionDesprendibles = () => {
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [file, setFile] = useState(null);
    const [desprendibles, setDesprendibles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [autorizado, setAutorizado] = useState(false);

    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user"));

    // Verificar permisos al montar el componente
    useEffect(() => {
        if (!user) {
            setMessage('No hay usuario autenticado.');
            return;
        }

        const rolNombre = user.rol?.toLowerCase() || '';
        const departamento = user.departamento?.toLowerCase() || '';
        const tienePermisos = rolNombre === 'administrador' || departamento === 'recursos humanos';

        setAutorizado(tienePermisos);

        if (!tienePermisos) {
            setMessage('No tienes permisos para gestionar los desprendibles.');
            return;
        }

        fetchDesprendibles();
    }, []);

    const fetchDesprendibles = async () => {
        try {
            setLoading(true);
            const response = await api.get('/tramites/desprendibles');
            if (Array.isArray(response.data)) {
                setDesprendibles(response.data);
            } else if (response.data && Array.isArray(response.data.data)) {
                setDesprendibles(response.data.data);
            } else {
                setDesprendibles([]);
                console.warn('La respuesta de /tramites/desprendibles no es un array:', response.data);
            }
        } catch (error) {
            setMessage('Error al cargar los desprendibles existentes.');
            console.error('Error fetching desprendibles:', error);
            setDesprendibles([]); // Ensure it's an array on error
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file) {
            setMessage('Por favor, seleccione un archivo.');
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('year', parseInt(year, 10)); // Convertir a número
        formData.append('month', parseInt(month, 10)); // Convertir a número

        try {
            setLoading(true);
            setMessage('Subiendo archivo...');
            const response = await api.post('/tramites/desprendibles', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setMessage(response.data.message || 'Archivo subido con éxito.');
            setFile(null);
            document.getElementById('file-input').value = '';
            fetchDesprendibles(); // Refresh the list
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Error al subir el archivo.';
            setMessage(errorMessage);
            console.error('Error uploading file:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (year, month) => {
        const months = [
            { value: 1, name: 'Enero' }, { value: 2, name: 'Febrero' }, { value: 3, name: 'Marzo' },
            { value: 4, name: 'Abril' }, { value: 5, name: 'Mayo' }, { value: 6, name: 'Junio' },
            { value: 7, name: 'Julio' }, { value: 8, name: 'Agosto' }, { value: 9, name: 'Septiembre' },
            { value: 10, name: 'Octubre' }, { value: 11, name: 'Noviembre' }, { value: 12, name: 'Diciembre' }
        ];
        if (!window.confirm(`¿Está seguro de que desea eliminar el paquete de desprendibles de ${months.find(m => m.value === month).name} de ${year}?`)) {
            return;
        }

        try {
            setLoading(true);
            setMessage('Eliminando paquete...');
            const response = await api.post(`/tramites/desprendibles/destroy`, {
                year: parseInt(year, 10),
                month: parseInt(month, 10)
            });
            setMessage(response.data.message || 'Paquete eliminado con éxito.');
            fetchDesprendibles(); // Refresh the list
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'Error al eliminar el paquete.';
            setMessage(errorMessage);
            console.error('Error deleting file:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 bg-white shadow-md rounded-lg">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">Gestión de Desprendibles de Pago</h2>

            {message && <div className={`p-3 mb-4 text-sm rounded-lg ${message.includes('Error') || message.includes('No tienes permisos') ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{message}</div>}

            {!autorizado ? (
                <div className="p-4 bg-yellow-100 text-yellow-700 rounded-lg">
                    <p>Solo los administradores y empleados de Recursos Humanos pueden gestionar los desprendibles de pago.</p>
                </div>
            ) : (
                <>
                    <form onSubmit={handleUpload} className="mb-8 p-4 border rounded-lg">
                        <h3 className="text-xl font-semibold mb-3">Subir Nuevo Paquete de Desprendibles</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label htmlFor="year" className="block text-sm font-medium text-gray-700">Año</label>
                                <select id="year" value={year} onChange={(e) => setYear(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500">
                                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(y => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="month" className="block text-sm font-medium text-gray-700">Mes</label>
                                <select id="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500">
                                    {[
                                        { value: 1, name: 'Enero' }, { value: 2, name: 'Febrero' }, { value: 3, name: 'Marzo' },
                                        { value: 4, name: 'Abril' }, { value: 5, name: 'Mayo' }, { value: 6, name: 'Junio' },
                                        { value: 7, name: 'Julio' }, { value: 8, name: 'Agosto' }, { value: 9, name: 'Septiembre' },
                                        { value: 10, name: 'Octubre' }, { value: 11, name: 'Noviembre' }, { value: 12, name: 'Diciembre' }
                                    ].map(m => <option key={m.value} value={m.value}>{m.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="file-input" className="block text-sm font-medium text-gray-700">Archivo PDF</label>
                                <input id="file-input" type="file" onChange={handleFileChange} accept=".pdf" className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"/>
                            </div>
                        </div>
                        <div className="mt-4">
                            <button type="submit" disabled={loading || !file} className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-400">
                                {loading ? 'Subiendo...' : 'Subir Archivo'}
                            </button>
                        </div>
                    </form>

                    <div>
                        <h3 className="text-xl font-semibold mb-3">Paquetes de Desprendibles Existentes</h3>
                        {loading && <p>Cargando lista...</p>}
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Año</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mes</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre del Archivo</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {Array.isArray(desprendibles) && desprendibles.length > 0 ? (
                                        desprendibles.map(d => {
                                            const monthsArray = [
                                                { value: 1, name: 'Enero' }, { value: 2, name: 'Febrero' }, { value: 3, name: 'Marzo' },
                                                { value: 4, name: 'Abril' }, { value: 5, name: 'Mayo' }, { value: 6, name: 'Junio' },
                                                { value: 7, name: 'Julio' }, { value: 8, name: 'Agosto' }, { value: 9, name: 'Septiembre' },
                                                { value: 10, name: 'Octubre' }, { value: 11, name: 'Noviembre' }, { value: 12, name: 'Diciembre' }
                                            ];
                                            return (
                                                <tr key={`${d.year}-${d.month}`}>
                                                    <td className="px-6 py-4 whitespace-nowrap">{d.year}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap">{monthsArray.find(m => m.value === d.month)?.name}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{d.filename}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                        <button onClick={() => handleDelete(d.year, d.month)} disabled={loading} className="text-red-600 hover:text-red-900 disabled:text-gray-400">
                                                            Eliminar
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="4" className="px-6 py-4 text-center text-gray-500">No hay paquetes de desprendibles cargados.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default GestionDesprendibles;
